import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ModuleIcon } from "@/components/ModuleIcon";
import { ModuleTitle, ProgereText } from "@/components/ProgereText";
import { ContractDialog } from "@/components/ContractDialog";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { PRICING_ICONS } from "@/data/pricing";
import { localizePricing } from "@/data/pricingI18n";
import { DiscountCalculator } from "@/components/DiscountCalculator";
import { PricePlanCard } from "@/components/PricePlanCard";
import { SEO } from "@/components/SEO";
import { EXAMPLE_PACKS, DISCOUNT_TABLE } from "@/data/basePrices";
import { Check, Crown, Info, Plus, LifeBuoy, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function PricingPage() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const { pricing: PRICING, support: SUPPORT_INCLUDED, p360: PROGERE_360 } = useMemo(() => localizePricing(lang), [lang]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [contract, setContract] = useState(null);
  const [payrollSel, setPayrollSel] = useState({}); // key: `${moduleKey}-${tier}` -> bool
  const [openModule, setOpenModule] = useState(PRICING[0]?.key || null);

  useEffect(() => window.scrollTo(0, 0), []);

  useEffect(() => {
    const mod = searchParams.get("module");
    if (mod && PRICING.some((m) => m.key === mod)) {
      setOpenModule(mod);
      setTimeout(() => {
        document.getElementById(`pricing-module-${mod}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const choose = (module, plan, addonOn) => {
    if (!user) {
      toast.info(t("calc.loginFirst"));
      navigate("/login");
      return;
    }
    const addon = plan.payroll || plan.takeoff;
    const addonKind = plan.payroll ? "PAIE" : plan.takeoff ? "TAKEOFF" : null;
    const addonActive = addonOn && addon;
    const name = addonActive ? `${module.name} ${plan.label} + ${addonKind} ${plan.label}` : `${module.name} ${plan.label}`;
    setContract({
      module: { key: module.key, name: module.name },
      plan: {
        key: `${module.key}-${plan.tier}${addonActive ? (plan.payroll ? "-paie" : "-takeoff") : ""}`,
        name,
        price: plan.price,
        period: "CAD/mois",
        tier: plan.tier,
      },
      payroll: addonActive ? `${addonKind} ${plan.label}` : null,
      addonKind: addonActive ? addonKind : null,
    });
  };

  return (
    <div className="min-h-screen progere-bg" data-testid="pricing-page">
      <SEO title="Tarification" path="/tarification" description="Forfaits PROGÈRE par module (Starter, Professionnel, Entreprise) avec rabais multi-modules. PUNCH, CRM, ERP, COMPTA, DEVIS AI — conforme QC (Loi 25, TPS/TVQ)." />
      <Header />

      {/* Intro */}
      <section className="relative pt-28 pb-14 overflow-hidden">
        <div className="absolute inset-0 pg-grid-bg opacity-30" />
        <div className="absolute top-0 left-1/3 w-[500px] h-[400px] pg-radial-gold" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#EAB308]">{t("pricing.eyebrow")}</span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
            <ProgereText />
            <span className="block text-white mt-2 text-2xl sm:text-3xl font-bold">{t("pricingPage.subtitle")}</span>
          </h1>
          <p className="mt-5 max-w-3xl text-white/65 text-lg leading-relaxed">
            {t("pricing.desc")}
          </p>
          <p className="mt-3 max-w-3xl text-white/55 leading-relaxed">
            {t("modules.desc")}
          </p>
        </div>
      </section>

      {/* Modules — accordéon */}
      <section className="pb-10">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 space-y-20">
          <div>
            <div className="flex items-center justify-between gap-4 mb-6">
              <div>
                <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#EAB308]">{t("pricingPage.ourModules")}</span>
                <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold text-white">
                  {t("pricingPage.clickA")} <span className="text-[#EAB308]">{t("pricingPage.plans")}</span>
                </h2>
              </div>
            </div>

            <div className="space-y-4">
              {PRICING.map((module) => {
                const isOpen = openModule === module.key;
                return (
                  <div
                    key={module.key}
                    id={`pricing-module-${module.key}`}
                    data-testid={`pricing-module-${module.key}`}
                    className={`overflow-hidden rounded-2xl border transition-colors ${
                      isOpen ? "border-[#EAB308]/40 bg-[#0a0a0a]" : "border-white/10 bg-[#0a0a0a] hover:border-white/25"
                    }`}
                  >
                    {/* En-tête cliquable */}
                    <button
                      data-testid={`pricing-toggle-${module.key}`}
                      onClick={() => setOpenModule((k) => (k === module.key ? null : module.key))}
                      aria-expanded={isOpen}
                      className="w-full flex items-center gap-4 p-5 sm:p-6 text-left"
                    >
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-[#EAB308]/40 bg-[#EAB308]/10">
                        <ModuleIcon name={PRICING_ICONS[module.key]} className="w-6 h-6 text-[#EAB308]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl sm:text-2xl font-extrabold"><ModuleTitle name={module.name} /></h3>
                        <p className="mt-0.5 text-sm text-white/55 truncate">{module.subtitle}</p>
                      </div>
                      <span className="hidden sm:block text-xs font-mono uppercase tracking-wider text-white/40 mr-2">
                        {isOpen ? t("pricingPage.hide") : t("pricingPage.seePlans")}
                      </span>
                      <ChevronDown
                        className={`w-6 h-6 shrink-0 text-[#EAB308] transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>

                    {/* Contenu déroulant */}
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          key="content"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="px-5 sm:px-6 pb-6 pt-5">
                            {module.note && (
                              <div className="flex items-start gap-2 rounded-lg border border-[#EAB308]/25 bg-[#EAB308]/[0.06] px-4 py-3 text-sm text-[#f3d98a]">
                                <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#EAB308]" />
                                {module.note}
                              </div>
                            )}
                            <div className={`grid gap-6 md:grid-cols-3 ${module.note ? "mt-8" : "mt-2"}`}>
                              {module.plans.map((plan) => {
                                const selKey = `${module.key}-${plan.tier}`;
                                return (
                                  <PricePlanCard
                                    key={plan.tier}
                                    module={module}
                                    plan={plan}
                                    onChoose={choose}
                                    payrollOn={payrollSel[selKey]}
                                    togglePayroll={() => setPayrollSel((s) => ({ ...s, [selKey]: !s[selKey] }))}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>

          {/* PROGÈRE SUPPORT — inclus dans tous les forfaits */}
          <div
            data-testid="support-included"
            className="rounded-3xl border border-[#EAB308]/40 bg-gradient-to-br from-[#EAB308]/[0.07] via-[#0d0b05] to-black p-8 lg:p-10"
          >
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-[#EAB308]/40 bg-[#EAB308]/10">
                  <LifeBuoy className="w-6 h-6 text-[#EAB308]" />
                </div>
                <div>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-green-500/40 bg-green-500/15 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-green-400">
                    {t("pricingPage.includedAll")}
                  </span>
                  <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold"><ModuleTitle name="PROGÈRE SUPPORT" /></h2>
                  <p className="mt-2 max-w-2xl text-white/60">
                    {SUPPORT_INCLUDED.subtitle} {t("pricingPage.supportDesc")}
                  </p>
                  <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                    {SUPPORT_INCLUDED.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-white/70">
                        <Check className="w-4 h-4 mt-0.5 shrink-0 text-green-500" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <button
                data-testid="support-cta"
                onClick={() => navigate("/support")}
                className="shrink-0 rounded-lg h-12 px-8 font-semibold bg-[#EAB308] text-black hover:opacity-90 transition-opacity"
              >
                {t("pricingPage.talkTech")}
              </button>
            </div>
          </div>

          {/* PROGÈRE 360 */}
          <div
            data-testid="pricing-360"
            className="relative rounded-3xl border-2 border-[#EAB308]/60 bg-gradient-to-br from-[#EAB308]/[0.08] via-[#0d0b05] to-black p-8 lg:p-10 pg-glow-gold"
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EAB308] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-black">
                  <Crown className="w-3.5 h-3.5" /> {t("pricingPage.fullEco")}
                </span>
                <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold"><ModuleTitle name="PROGÈRE 360" /></h2>
                <p className="mt-1 text-white/60">{PROGERE_360.subtitle} — <span className="text-white font-semibold">{t("pricingPage.onDemand")}</span></p>
              </div>
              <button
                data-testid="pricing-360-cta"
                onClick={() => navigate("/#demo")}
                className="shrink-0 rounded-lg h-12 px-8 font-semibold bg-[#EAB308] text-black hover:opacity-90 transition-opacity"
              >
                {t("pricingPage.requestQuote")}
              </button>
            </div>

            <div className="mt-8 grid gap-8 md:grid-cols-2">
              <div>
                <div className="text-xs font-mono uppercase tracking-wider text-[#EAB308]">{t("pricingPage.included")}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {PROGERE_360.includes.map((m) => (
                    <span key={m} className="rounded-full border border-[#EAB308]/40 bg-[#EAB308]/10 px-3 py-1 text-xs font-semibold text-[#EAB308]">{m}</span>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-wider text-[#EAB308]">{t("pricingPage.benefits")}</div>
                <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                  {PROGERE_360.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-white/75">
                      <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#EAB308]" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-white/35">
            {t("pricingPage.stripeNote")}
          </p>

          <div className="mx-auto max-w-2xl rounded-lg border border-[#EAB308]/25 bg-[#EAB308]/[0.05] px-4 py-3 text-center" data-testid="pricing-tax-note">
            <p className="text-xs text-white/70">
              <span className="font-semibold text-[#EAB308]">TPS / TVQ</span> — {t("calc.disclaimer")}
            </p>
          </div>

          {/* Rabais multi-modules */}
          <div data-testid="multi-discount-section" className="pt-6">
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#EAB308]">{t("pricingPage.savings")}</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-white">
              {t("pricingPage.saveA")} <span className="text-[#EAB308]">{t("pricingPage.modules")}</span>
            </h2>
            <p className="mt-3 max-w-3xl text-white/60 text-lg leading-relaxed">
              {t("pricingPage.saveDesc")}
            </p>

            {/* Table */}
            <div className="mt-8 overflow-x-auto rounded-2xl border border-white/10">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-white/[0.04] text-white/50 text-left">
                    <th className="px-5 py-3 font-medium">{t("pricingPage.tblCount")}</th>
                    <th className="px-5 py-3 font-medium">{t("pricingPage.tblDiscount")}</th>
                    <th className="px-5 py-3 font-medium">{t("pricingPage.tblExample")}</th>
                  </tr>
                </thead>
                <tbody>
                  {DISCOUNT_TABLE.map((r) => (
                    <tr key={r.count} className="border-t border-white/8">
                      <td className="px-5 py-3 text-white font-medium">{r.count}</td>
                      <td className="px-5 py-3 text-[#EAB308] font-semibold">{r.discount}</td>
                      <td className="px-5 py-3 text-white/60">{r.example}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Calculator */}
            <div className="mt-10">
              <DiscountCalculator />
            </div>

            {/* Example cards */}
            <h3 className="mt-14 text-2xl font-extrabold text-white">{t("pricingPage.priceExamples")}</h3>
            <div className="mt-6 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {EXAMPLE_PACKS.map((p) => (
                <div key={p.title} data-testid={`example-pack-${p.title}`} className="flex flex-col rounded-2xl border border-white/10 bg-[#0a0a0a] p-6">
                  <h4 className="text-lg font-bold text-white">{p.title}</h4>
                  <ul className="mt-3 space-y-1.5 flex-1">
                    {p.modules.map((m) => (
                      <li key={m} className="flex items-start gap-2 text-sm text-white/70">
                        <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#EAB308]" /> {m}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 space-y-1.5 border-t border-white/10 pt-4 text-sm">
                    <div className="flex justify-between text-white/50"><span>{t("pricingPage.regular")}</span><span className="font-mono">{p.regular}</span></div>
                    <div className="flex justify-between text-white/50"><span>{t("pricingPage.discount")}</span><span className="font-mono text-[#EAB308]">{p.discount}</span></div>
                    <div className="flex justify-between text-white/50"><span>{t("pricingPage.saving")}</span><span className="font-mono text-green-400">{p.saving}</span></div>
                    <div className="flex justify-between text-white font-bold pt-1"><span>{t("pricingPage.final")}</span><span className="font-mono">{p.final}</span></div>
                  </div>
                  <p className="mt-3 text-xs text-white/45">{p.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {contract && (
        <ContractDialog
          module={contract.module}
          plan={contract.plan}
          payroll={contract.payroll}
          addonKind={contract.addonKind}
          onClose={() => setContract(null)}
        />
      )}

      <Footer />
    </div>
  );
}
